import React from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'
import { IconX, IconChevronDown } from '@tabler/icons-react'
import { FloatingPortal, FloatingFocusManager } from '@floating-ui/react'
import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { SearchableDropdownResults } from './SearchableDropdownResults'
import {
  useSearchableDropdown,
  useDropdownFloating,
  focusDropdownTrigger,
} from './dropdowns.helpers'

import './SearchableDropdown.scss'

/**
 * @import import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
 * @param {object} props
 * @param {string} [props.buttonLabel] Trigger text while nothing is selected
 * @param {object} [props.buttonProps] Props spread onto the trigger Button
 * @param {object} [props.inputProps] Props spread onto the search input
 * @param {Array<object>} [props.options] Options with value, label, and optional labelNode, meta, hasSeparator, icon, description, disabled, actions, infoTooltipDetails; groups nest options under a label
 * @param {boolean} [props.loading] Shows a loader in the results list
 * @param {(value: *, updatedValue: *) => void} [props.handleSelect] Called with the picked value, and the full selection for multi-select
 * @param {string} [props.placement] Floating placement of the results panel
 * @param {boolean} [props.hideSearch] Hides the search input
 * @param {*} [props.selected] Current value, or an array of selected options or values for multi-select
 * @param {React.ReactNode} [props.icon] Replaces the chevron on the trigger
 * @param {React.ReactNode} [props.additionalIcon] Icon rendered before the trigger label
 * @param {Array<object>} [props.actions] Header actions with key, label, fn
 * @param {string} [props.dataTestId] Suffix for the trigger data-testid
 * @param {string} [props.pinnedAccessor] Option accessor whose truthy options are starred and pinned to the top
 * @param {string} [props.additionalLabel] Bold prefix before the trigger label
 * @param {boolean} [props.showResultsHeader] Shows a header row above the results
 * @param {React.ReactNode} [props.resultsHeader] Explanatory note rendered above the results
 * @param {React.ReactNode} [props.triggerMeta] Muted secondary text after the trigger label
 * @param {boolean} [props.isClearable] Shows a clear button on the trigger, and in the results header, while there is a selection
 * @param {(label: string) => void} [props.handleCreate] Shows a create input below the results; called with the entered text
 * @param {boolean} [props.fitContent] Sizes the results panel to its content
 * @param {boolean} [props.wide] Widens the results panel, for rows with meta or long descriptions
 * @param {boolean} [props.isDisabled] Disables the trigger
 * @param {string} [props.disabledTooltip] Tooltip shown while disabled
 * @param {string} [props.tooltip] Tooltip shown on the trigger while enabled
 * @param {boolean} [props.selectedOptionFirst] Sorts the selected option to the top of the results. A `hasSeparator` option's divider travels with it, so a fixed running order wants this off (and a `pinnedAccessor` that matches nothing)
 * @param {boolean} [props.hideLabel] Hides the selected label on the trigger
 * @param {boolean} [props.alwaysOpen] Renders the results inline without a trigger button
 * @param {boolean} [props.isMultiSelect] Checkbox multi-select; keeps the results open after selecting
 */
export const SearchableDropdown = ({
  buttonLabel,
  buttonProps = {},
  inputProps = {},
  options = [],
  loading,
  handleSelect = () => {},
  placement = 'bottom-start',
  hideSearch,
  selected,
  icon,
  additionalIcon,
  actions,
  dataTestId = '',
  pinnedAccessor = 'is_favorite',
  additionalLabel,
  showResultsHeader = false,
  resultsHeader,
  triggerMeta,
  isClearable = false,
  handleCreate = null,
  fitContent = false,
  wide = false,
  isDisabled = false,
  disabledTooltip,
  tooltip,
  selectedOptionFirst = true,
  hideLabel = false,
  alwaysOpen = false,
  isMultiSelect = false,
}) => {
  const { refs, floatingStyles, getReferenceProps, getFloatingProps, context, open, setOpen } =
    useDropdownFloating({ placement, isDisabled })

  const { sortedOptions, emptyValue, isNestedSelect, selectedLabel, normalizedSelected } =
    useSearchableDropdown({
      options,
      selected,
      isMultiSelect,
      selectedOptionFirst,
      pinnedAccessor,
      buttonLabel,
      handleCreate,
    })

  const hasSelection = isMultiSelect ? !isEmpty(normalizedSelected) : !isNil(selected)

  const buttonTestId = dataTestId
    ? `searchable-dropdown-button-${dataTestId}`
    : 'searchable-dropdown-button'

  const resultsContent = (
    <SearchableDropdownResults
      hideSearch={hideSearch}
      options={sortedOptions}
      setOpen={setOpen}
      handleSelect={(...args) => {
        handleSelect(...args)

        if (!alwaysOpen && !isMultiSelect) {
          setOpen(false)
          focusDropdownTrigger(refs.domReference.current)
        }
      }}
      loading={loading}
      inputProps={inputProps}
      selected={normalizedSelected}
      actions={actions}
      pinnedAccessor={pinnedAccessor}
      showResultsHeader={showResultsHeader}
      resultsHeader={resultsHeader}
      handleCreate={handleCreate}
      fitContent={fitContent}
      wide={wide}
      alwaysOpen={alwaysOpen}
      isMultiSelect={isMultiSelect}
      emptyValue={emptyValue}
      isNestedSelect={isNestedSelect}
      isClearable={isClearable}
    />
  )

  const trigger = (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}
      data-testid="searchable-dropdown"
      className={classNames({ 'searchable-dropdown-trigger': !alwaysOpen })}
    >
      {!alwaysOpen && (
        <Button data-testid={buttonTestId} disabled={isDisabled} active={open} {...buttonProps}>
          {additionalIcon}
          {additionalLabel && <strong>{additionalLabel}</strong>}
          {!hideLabel && <span className="searchable-dropdown-button-label">{selectedLabel}</span>}
          {triggerMeta && <span className="searchable-dropdown-button-meta">{triggerMeta}</span>}
          {isClearable && hasSelection && (
            <div
              className="searchable-dropdown__clear-button"
              onClick={(event) => {
                event.stopPropagation()

                handleSelect(emptyValue, emptyValue)

                if (!alwaysOpen) {
                  setOpen(false)
                }
              }}
            >
              <IconX className="icon-svg" />
            </div>
          )}
          {icon || <IconChevronDown className="searchable-dropdown-button-chevron" />}
        </Button>
      )}

      {alwaysOpen && resultsContent}
    </div>
  )

  const dropdown = open && !alwaysOpen && (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
        <div
          ref={refs.setFloating}
          className="searchable-dropdown-floating"
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {resultsContent}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )

  const content = (
    <>
      {trigger}
      {dropdown}
    </>
  )

  if (isDisabled && disabledTooltip) {
    return (
      <InteractiveTooltip content={disabledTooltip} placement="top">
        <span className="searchable-dropdown-tooltip-anchor">{content}</span>
      </InteractiveTooltip>
    )
  }

  if (!isDisabled && tooltip) {
    return (
      <InteractiveTooltip content={tooltip} placement="top" hide={open}>
        <span className="searchable-dropdown-tooltip-anchor">{content}</span>
      </InteractiveTooltip>
    )
  }

  return content
}
